import { useSyncExternalStore } from 'react';

import styles from '../ui/KnowledgeBase.module.scss';

/**
 * The article video, and where it lives right now.
 *
 * The player itself is owned by KbVideoHost at the app root, not by the
 * article: the article only offers a slot, and the host element is moved
 * into it. Moving the element keeps the media element alive, so playback
 * never restarts. When the article goes away mid-play the same element is
 * moved to the app corner instead, and the reader can walk anywhere with
 * the video still running.
 *
 * One video plays at a time. A running video keeps the corner while the
 * reader opens other pages, including pages that carry their own video:
 * such a page waits, and starting it closes the corner card.
 */
export type KbVideoSession = {
  src: string;
  poster?: string;
  title: string;
  detached: boolean;
  // The reader asked for this video on purpose: it starts as soon as the
  // player has it.
  autoplay: boolean;
  // Where the video came from: the corner card offers a way back there.
  article?: { market: string; slug: string };
};

type KbVideoTarget = {
  src: string;
  poster?: string;
  title: string;
  article?: { market: string; slug: string };
};

let session: KbVideoSession | null = null;

// The element the player is rendered into. It is created once and moved
// between the article slot and the corner, so React never unmounts it.
let hostElement: HTMLDivElement | null = null;
let cornerElement: HTMLDivElement | null = null;

const listeners = new Set<() => void>();

const emit = () => {
  listeners.forEach((listener) => listener());
};

const ensureHostElement = (): HTMLDivElement => {
  if (!hostElement) {
    hostElement = document.createElement('div');
    hostElement.dataset.kbVideoHost = '';
  }

  return hostElement;
};

const ensureCornerElement = (): HTMLDivElement => {
  if (!cornerElement) {
    cornerElement = document.createElement('div');
    cornerElement.className = styles.floatingCorner ?? '';
    document.body.appendChild(cornerElement);
  }

  return cornerElement;
};

const playerVideo = (): HTMLVideoElement | null =>
  hostElement?.querySelector('video') ?? null;

export const getKbVideoHostElement = (): HTMLDivElement => ensureHostElement();

export const getKbVideoSession = (): KbVideoSession | null => session;

/** A video other than this one runs in the corner: this slot has to wait. */
const isKbVideoWaiting = (src: string): boolean =>
  Boolean(session?.detached && session.src !== src);

/**
 * Put the player in an article slot. The same file keeps playing; a new file
 * starts from the beginning.
 *
 * Returns false when another video is floating: that one keeps the corner
 * until the reader starts this video here.
 */
export const attachKbVideo = (
  next: KbVideoTarget,
  slot: HTMLElement | null,
  options?: { autoplay?: boolean },
): boolean => {
  if (!slot) return false;

  if (isKbVideoWaiting(next.src)) return false;

  const autoplay = options?.autoplay ?? false;

  // A repeat attach of the video already in this slot keeps its state,
  // including a pending autoplay request.
  if (session && !session.detached && session.src === next.src && !autoplay) {
    return true;
  }

  session = { ...next, detached: false, autoplay };

  const host = ensureHostElement();

  if (host.parentElement !== slot) slot.appendChild(host);

  emit();

  return true;
};

/**
 * The article is gone. A playing video moves to the app corner and keeps
 * going; a paused or finished one just stops. The source guards against a
 * stale article taking down a video that already replaced it.
 */
export const detachKbVideo = (src: string) => {
  if (!session || session.src !== src) return;

  const video = playerVideo();
  const playing = Boolean(video && !video.paused && !video.ended);

  if (!playing) {
    closeKbVideo();

    return;
  }

  session = { ...session, detached: true, autoplay: false };
  ensureCornerElement().appendChild(ensureHostElement());
  emit();
};

/** Start the video the session already holds, once the player has its file. */
export const playKbVideo = () => {
  const video = playerVideo();

  if (!video) return;

  // jsdom returns nothing here, so the promise is not taken for granted.
  const started: Promise<void> | undefined = video.play();

  // A rejected promise means the browser refused to start; the reader still
  // has the play button.
  started?.catch(() => {});
};

export const closeKbVideo = () => {
  playerVideo()?.pause();
  session = null;
  emit();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

export const useKbVideoSession = (): KbVideoSession | null =>
  useSyncExternalStore(subscribe, () => session);
